<template>
    <div class="campaign__container">
        <side-menu-header :role="user.role"></side-menu-header>
        
        <div class="campaign__body">
            <!-- Stepper Progress Header -->
            <div class="stepper__container">
                <div class="stepper__wrapper">
                    <div 
                        v-for="step in steps" 
                        :key="step.number" 
                        class="stepper__item"
                        :class="{
                            'stepper__item--active': currentStep === step.number,
                            'stepper__item--completed': currentStep > step.number,
                            'stepper__item--clickable': maxStepReached >= step.number
                        }"
                        @click="handleStepClick(step.number)"
                    >
                        <div class="stepper__circle">
                            <span v-if="currentStep > step.number">✓</span>
                            <span v-else>{{ step.number }}</span>
                        </div>
                        <div class="stepper__label">
                            <span class="stepper__step-tag">Step {{ step.number }}</span>
                            <span class="stepper__title">{{ step.title }}</span>
                        </div>
                        <div v-if="step.number < steps.length" class="stepper__connector"></div>
                    </div>
                </div>
            </div>

            <!-- Page Title -->
            <div class="campaign__header-wrap">
                <h1 class="campaign__main-title">
                    {{ isEditMode ? 'Edit Campaign' : 'Create New Campaign' }}
                </h1>
                <p class="campaign__subtitle">
                    Complete each step below to configure your campaign targeting, creative, and budget.
                </p>
            </div>

            <!-- ================= STEP 1: CAMPAIGN DETAILS ================= -->
            <div 
                id="step-1" 
                class="step-card"
                :class="getStepClass(1)"
            >
                <div class="step-card__header" @click="maxStepReached >= 1 && goToStep(1)">
                    <div class="step-card__badge">1</div>
                    <div class="step-card__header-info">
                        <h2>Campaign Details</h2>
                        <p>Start with the campaign name and client information.</p>
                    </div>
                    <div class="step-card__status">
                        <span v-if="currentStep === 1" class="status-pill status-pill--active">In Progress</span>
                        <span v-else-if="currentStep > 1" class="status-pill status-pill--done">Completed (Edit)</span>
                        <span v-else class="status-pill status-pill--locked">Upcoming</span>
                    </div>
                </div>

                <div v-show="currentStep === 1 || currentStep > 1" class="step-card__content">
                    <div class="form-row">
                        <div class="form-group flex-1">
                            <label class="form-label">Campaign Name <span class="required">*</span></label>
                            <input 
                                type="text" 
                                class="form-input" 
                                placeholder="e.g. Summer Brand Launch" 
                                v-model="campaign.name" 
                                :disabled="currentStep !== 1"
                            />
                            <span class="error-msg" v-if="errors && errors.name">{{ errors.name }}</span>
                        </div>

                        <div class="form-group flex-1" v-if="user.role !== 'advertiser'">
                            <label class="form-label">Client Name <span class="required">*</span></label>
                            <input 
                                type="text" 
                                class="form-input" 
                                placeholder="e.g. Acme Corporation" 
                                v-model="campaign.client" 
                                :disabled="currentStep !== 1"
                            />
                            <span class="error-msg" v-if="errors && errors.client">{{ errors.client }}</span>
                        </div>
                    </div>

                    <!-- Device Group (Advanced) - Managers and Admins only -->
                    <div class="advanced-section" v-if="user.role !== 'advertiser'">
                        <button type="button" class="btn-toggle-advanced" @click="showAdvancedVehicle = !showAdvancedVehicle">
                            <span>{{ showAdvancedVehicle ? 'Hide' : 'Show' }} Device Group (Advanced)</span>
                            <span class="toggle-icon">{{ showAdvancedVehicle ? '▲' : '▼' }}</span>
                        </button>

                        <div v-show="showAdvancedVehicle" class="advanced-content">
                            <div class="form-group" style="max-width: 400px;">
                                <label class="form-label" for="device-group-input">Device Group (Optional)</label>
                                <input
                                    id="device-group-input"
                                    type="text"
                                    class="form-input"
                                    maxlength="100"
                                    placeholder="e.g. West-Coast-Fleet"
                                    v-model="campaign.device_group"
                                    @blur="campaign.device_group = sanitizeDeviceGroup(campaign.device_group)"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="step-card__actions" v-if="currentStep === 1">
                        <button type="button" class="btn-continue" @click="nextStep(1)">
                            Continue to Schedule & Timing →
                        </button>
                    </div>
                </div>
            </div>

            <!-- ================= STEP 2: WHERE AND WHEN (SCHEDULE & TIMINGS) ================= -->
            <div 
                id="step-2" 
                class="step-card"
                :class="getStepClass(2)"
            >
                <div class="step-card__header" @click="maxStepReached >= 2 && goToStep(2)">
                    <div class="step-card__badge">2</div>
                    <div class="step-card__header-info">
                        <h2>When and When? (Schedule & Timings)</h2>
                        <p>Set the campaign flight dates and daily broadcast window.</p>
                    </div>
                    <div class="step-card__status">
                        <span v-if="currentStep === 2" class="status-pill status-pill--active">In Progress</span>
                        <span v-else-if="currentStep > 2" class="status-pill status-pill--done">Completed (Edit)</span>
                        <span v-else class="status-pill status-pill--locked">Upcoming</span>
                    </div>
                </div>

                <div v-show="currentStep === 2 || currentStep > 2" class="step-card__content">
                    <!-- Date Section with Helpers -->
                    <div class="schedule-block">
                        <div class="block-title-row">
                            <label class="form-label bold-label">Campaign Flight Dates <span class="required">*</span></label>
                            <div class="helper-pill-group">
                                <span class="helper-label">Quick helpers:</span>
                                <button type="button" class="helper-btn" @click="applyDatePreset('next_month')">
                                    📅 Start Next Month
                                </button>
                                <button type="button" class="helper-btn" @click="applyDatePreset('one_month')">
                                    1 Month Duration
                                </button>
                                <button type="button" class="helper-btn" @click="applyDatePreset('two_weeks')">
                                    2 Weeks
                                </button>
                                <button type="button" class="helper-btn" @click="applyDatePreset('three_months')">
                                    3 Months
                                </button>
                            </div>
                        </div>

                        <div class="form-row date-picker-row">
                            <div class="form-group flex-1">
                                <label class="form-label">Start Date</label>
                                <v-date-picker 
                                    mode="single" 
                                    :masks="{ input: ['MM-DD-YYYY']}" 
                                    :input-props='{class: "datepicker form-input", disabled: currentStep !== 2}' 
                                    v-model='campaign.start_date'
                                />
                            </div>
                            <div class="form-group flex-1">
                                <label class="form-label">End Date</label>
                                <v-date-picker 
                                    mode="single" 
                                    :masks="{ input: ['MM-DD-YYYY']}" 
                                    :input-props='{class: "datepicker form-input", disabled: currentStep !== 2}' 
                                    v-model='campaign.end_date'
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Timings Section (Daytime/Nighttime Playback) -->
                    <div class="schedule-block mt-4">
                        <div class="block-title-row">
                            <div>
                                <label class="form-label bold-label">Daily Broadcast Hours <span class="required">*</span></label>
                                <p class="helper-description">Time of day when the ads will display on vehicle screens.</p>
                            </div>
                            <div class="helper-pill-group">
                                <span class="helper-label">Timing presets:</span>
                                <button 
                                    type="button" 
                                    class="helper-btn" 
                                    :class="{'helper-btn--active': timingPreset === 'night'}" 
                                    @click="applyTimingPreset('night')"
                                >
                                    🌙 Night Time (6 PM – 6 AM) [Default]
                                </button>
                                <button 
                                    type="button" 
                                    class="helper-btn" 
                                    :class="{'helper-btn--active': timingPreset === '24/7'}" 
                                    @click="applyTimingPreset('24/7')"
                                >
                                    ⏱️ 24/7 (All Day)
                                </button>
                                <button 
                                    type="button" 
                                    class="helper-btn" 
                                    :class="{'helper-btn--active': timingPreset === 'evening'}" 
                                    @click="applyTimingPreset('evening')"
                                >
                                    🌆 Evening Rush (4 PM – 8 PM)
                                </button>
                            </div>
                        </div>

                        <div class="form-row time-picker-row">
                            <div class="form-group flex-1">
                                <label class="form-label">Start Time</label>
                                <el-time-picker
                                    v-model="campaign.start_time"
                                    format="hh:mm A"
                                    value-format="HH:mm"
                                    placeholder="06:00 PM"
                                    :disabled="currentStep !== 2"
                                    @change="onCustomTimeChange"
                                >
                                </el-time-picker>
                            </div>
                            <div class="form-group flex-1">
                                <label class="form-label">End Time</label>
                                <el-time-picker
                                    v-model="campaign.end_time"
                                    format="hh:mm A"
                                    value-format="HH:mm"
                                    placeholder="06:00 AM"
                                    :disabled="currentStep !== 2"
                                    @change="onCustomTimeChange"
                                >
                                </el-time-picker>
                            </div>
                        </div>
                        <span class="error-msg" v-if="errors && errors.time">{{ errors.time }}</span>
                    </div>

                    <div class="step-card__actions" v-if="currentStep === 2">
                        <button type="button" class="btn-back" @click="goToStep(1)">← Back</button>
                        <button type="button" class="btn-continue" @click="nextStep(2)">Continue to Location →</button>
                    </div>
                </div>
            </div>

            <!-- ================= STEP 3: LOCATION SELECTION ================= -->
            <div 
                id="step-3" 
                class="step-card"
                :class="getStepClass(3)"
            >
                <div class="step-card__header" @click="maxStepReached >= 3 && goToStep(3)">
                    <div class="step-card__badge">3</div>
                    <div class="step-card__header-info">
                        <h2>Campaign Location</h2>
                        <p>Choose target metropolitan area or set precise geofencing boundaries.</p>
                    </div>
                    <div class="step-card__status">
                        <span v-if="currentStep === 3" class="status-pill status-pill--active">In Progress</span>
                        <span v-else-if="currentStep > 3" class="status-pill status-pill--done">Completed (Edit)</span>
                        <span v-else class="status-pill status-pill--locked">Upcoming</span>
                    </div>
                </div>

                <div v-show="currentStep === 3 || currentStep > 3" class="step-card__content">
                    <div class="location-top-bar">
                        <div class="form-group location-dropdown-group">
                            <label class="form-label bold-label">Select Target City / Market <span class="required">*</span></label>
                            <div class="select-wrapper">
                                <select 
                                    class="form-select" 
                                    v-model="selectedCity" 
                                    @change="onCitySelected(selectedCity)"
                                    :disabled="currentStep !== 3"
                                >
                                    <option v-for="city in cityOptions" :key="city" :value="city">
                                        {{ city }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div class="form-group precise-geofence-toggle-wrap">
                            <label class="form-label bold-label">&nbsp;</label>
                            <label class="checkbox-toggle-card" :class="{'checkbox-toggle-card--active': showPreciseGeofencing}">
                                <input 
                                    type="checkbox" 
                                    v-model="showPreciseGeofencing" 
                                    :disabled="currentStep !== 3"
                                />
                                <div class="toggle-text">
                                    <span class="toggle-title">📍 Select precise geo-fencing</span>
                                    <span class="toggle-desc">Open interactive map, custom radius & ZIP codes</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <!-- Selected Location Summary Pill -->
                    <div class="city-summary-pill" v-if="!showPreciseGeofencing">
                        <span class="pin-icon">📍</span>
                        <span>Active Target: <strong>{{ selectedCity }}</strong> (Default Market Coverage across Adway Vehicles)</span>
                    </div>

                    <!-- Existing Map & Geofence Section (Opens when toggle is checked) -->
                    <div v-show="showPreciseGeofencing" class="campaign__location mt-4">
                        <h4 class="campaign__info" style="margin-top: 10px; margin-bottom: 12px; font-size: 18px;">
                            Precise Geofencing & Address / ZIP Targeting
                        </h4>
                        
                        <div class="row location__row">
                            <div class="location__details">
                                <div class="google-type-container">
                                    <div class="google-type-holder" @click="mapTypeChange('zipcode')" :class="{'active-google-type-holder' : mapTypeActive === 'zipcode'}">
                                        <div class="google-type-text" id="google-type-text">Select location by Zipcode</div>
                                        <zipcode-icon></zipcode-icon>
                                    </div>

                                    <div class="google-type-holder" @click="mapTypeChange('geofence')" :class="{'active-google-type-holder' : mapTypeActive === 'geofence'}">
                                        <div class="google-type-text" id="google-type-text">Select location by Geofence</div>
                                        <geofence-icon></geofence-icon>
                                    </div>
                                </div>

                                <label class="pair__label">Search location by address</label>
                                <GmapAutocomplete class="form-input address-autocomplete-input" :value="campaign.address" @place_changed="addressChanged" :disabled="mapTypeActive==='zipcode'" placeholder="Enter address or landmark"></GmapAutocomplete>
                                <label class="error" v-if="errors && errors.address">{{ errors.address }}</label>

                                <p class="text-center my-2 text-muted">OR</p>

                                <label class="pair__label">Search location by zip code</label>
                                <div class="zip__input">
                                    <p v-for="zip in campaign.ZIP_codes" :key="zip">
                                        {{ zip }}
                                        <svg @click="removeZIP(zip)" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-x">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </p>
                                    <input type="text" v-on:keydown="checkKey" v-model="zipCode" :disabled="mapTypeActive==='geofence'" placeholder="Type ZIP and hit Enter" />
                                </div>
                                <label class="error" v-if="errors && errors.zip">{{ errors.zip }}</label>

                                <label class="upload__label" for="upload">
                                    <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5.24399 6.78613L8.11899 3.91113V13.2111C8.11511 13.3228 8.13328 13.4342 8.17244 13.5389C8.2116 13.6435 8.271 13.7395 8.34723 13.8212C8.42347 13.9029 8.51505 13.9688 8.61676 14.0151C8.71847 14.0614 8.8283 14.0873 8.93999 14.0911H8.99499C9.10668 14.095 9.21804 14.0768 9.32271 14.0377C9.42738 13.9985 9.52331 13.9391 9.60503 13.8629C9.68674 13.7867 9.75264 13.6951 9.79896 13.5934C9.84528 13.4917 9.87112 13.3818 9.87499 13.2701V3.91113L12.725 6.75613C12.8853 6.92201 13.1044 7.01806 13.335 7.02349C13.5656 7.02892 13.7891 6.94328 13.957 6.78513L13.986 6.75613C14.1415 6.58872 14.228 6.36866 14.228 6.14013C14.228 5.91161 14.1415 5.69155 13.986 5.52413L9.61499 1.15313C9.44758 0.997586 9.22751 0.911133 8.99899 0.911133C8.77047 0.911133 8.5504 0.997586 8.38299 1.15313L4.01199 5.52413C3.85644 5.69155 3.76999 5.91161 3.76999 6.14013C3.76999 6.36866 3.85644 6.58872 4.01199 6.75613C4.17213 6.92215 4.39124 7.01838 4.62183 7.024C4.85243 7.02962 5.07595 6.94416 5.24399 6.78613Z" fill="white"/>
                                        <path d="M16.539 9.78225C16.4273 9.77837 16.3159 9.79654 16.2113 9.8357C16.1066 9.87486 16.0107 9.93425 15.9289 10.0105C15.8472 10.0867 15.7813 10.1783 15.735 10.28C15.6887 10.3817 15.6629 10.4916 15.659 10.6032V14.5632C15.6538 14.6393 15.6213 14.7108 15.5674 14.7647C15.5136 14.8186 15.442 14.8511 15.366 14.8563H2.63399C2.55797 14.8511 2.4864 14.8186 2.43253 14.7647C2.37866 14.7108 2.34613 14.6393 2.34099 14.5632V10.6633C2.34486 10.5516 2.3267 10.4402 2.28754 10.3355C2.24837 10.2309 2.18898 10.1349 2.11274 10.0532C2.03651 9.9715 1.94492 9.9056 1.84321 9.85928C1.74151 9.81296 1.63168 9.78712 1.51999 9.78325H1.45999C1.34383 9.78108 1.22842 9.80236 1.12067 9.84581C1.01292 9.88927 0.915046 9.95401 0.832894 10.0362C0.750741 10.1183 0.686003 10.2162 0.642548 10.3239C0.599093 10.4317 0.577813 10.5471 0.579985 10.6633V14.5632C0.596298 15.1028 0.817939 15.6159 1.19966 15.9976C1.58138 16.3793 2.0944 16.6009 2.63399 16.6172H15.366C15.9056 16.6009 16.4186 16.3793 16.8003 15.9976C17.182 15.6159 17.4037 15.1028 17.42 14.5632V10.6633C17.4223 10.5469 17.4011 10.4313 17.3576 10.3234C17.3142 10.2155 17.2494 10.1174 17.1671 10.0351C17.0848 9.95286 16.9868 9.88806 16.8788 9.8446C16.7709 9.80115 16.6553 9.77994 16.539 9.78225Z" fill="white"/>
                                    </svg>
                                    Upload multiple ZIP\Postal code
                                    <input type="file" id="upload" @change="excellUploaded" v-show="false" accept=".xlsx, .xls, .csv" />
                                </label>
                                <label class="upload__label__note">*Upload files in Excel & csv format</label>
                            </div>

                            <div class="location__map">
                                <GmapMap
                                    ref="mapRef"
                                    :center="{lat: campaign.lat, lng: campaign.lon}"
                                    :zoom="10"
                                    :options="{mapTypeControl: false, streetViewControl: false, styles, zoomControl: false, gestureHandling: 'cooperative'}"
                                    style="width: 100%; height: 350px;"
                                    @click="mapClicked"
                                >
                                    <GmapCircle
                                        ref="mapCircle"
                                        v-if="campaign.address && mapTypeActive === 'geofence'"
                                        :center="{lat:campaign.lat, lng:campaign.lon}"
                                        :radius="campaign.distance"
                                        :clickable="true"
                                        :draggable="true"
                                        :editable="true"
                                        :options="{strokeColor: '#102130', strokeWeight: 2, fillColor: 'rgb(223, 242, 255)', fillOpacity: .5}"
                                    />
                                    <div v-if="mapTypeActive == 'zipcode'">
                                        <GmapPolygon
                                            v-for="(polygon, index) in polygons"
                                            :key="index"
                                            :path.sync="polygon"
                                            :options="{strokeColor: '#102130', strokeWeight: 2, fillColor: 'rgb(223, 242, 255)', fillOpacity: .5}"
                                        />
                                    </div>
                                </GmapMap>
                            </div>
                        </div>
                    </div>

                    <div class="step-card__actions" v-if="currentStep === 3">
                        <button type="button" class="btn-back" @click="goToStep(2)">← Back</button>
                        <button type="button" class="btn-continue" @click="nextStep(3)">Continue to Creative →</button>
                    </div>
                </div>
            </div>

            <!-- ================= STEP 4: CAMPAIGN CREATIVE ================= -->
            <div 
                id="step-4" 
                class="step-card"
                :class="getStepClass(4)"
            >
                <div class="step-card__header" @click="maxStepReached >= 4 && goToStep(4)">
                    <div class="step-card__badge">4</div>
                    <div class="step-card__header-info">
                        <h2>Upload Campaign Creative</h2>
                        <p>Upload your static image ($4 CPM) or video asset ($7 CPM).</p>
                    </div>
                    <div class="step-card__status">
                        <span v-if="currentStep === 4" class="status-pill status-pill--active">In Progress</span>
                        <span v-else-if="currentStep > 4" class="status-pill status-pill--done">Completed (Edit)</span>
                        <span v-else class="status-pill status-pill--locked">Upcoming</span>
                    </div>
                </div>

                <div v-show="currentStep === 4 || currentStep > 4" class="step-card__content">
                    <div class="detected-cpm-banner" v-if="imgUploaded || campaign.content_url">
                        <div class="detected-badge" :class="creativeType === 'video' ? 'detected-badge--video' : 'detected-badge--image'">
                            {{ creativeType === 'video' ? '🎥 Video Creative Detected' : '🖼️ Static Image Detected' }}
                        </div>
                        <div class="detected-cpm-text">
                            Rate: <strong>${{ currentCPM }}.00</strong> per 1,000 estimated impressions
                        </div>
                    </div>

                    <div class="add__campaign__creative">
                        <div class="row">
                            <div class="info__holder">
                                <div class="upload__container" @dragenter="dragEnter()" @dragleave="dragLeave()" :class="{'drag__enter': drag}">
                                    <div class="drag__img">
                                        <img src="./../../assets/upload_cards.svg" ref="dragIMG" />
                                    </div>
                                    <canvas id="dragImgCanvas"></canvas>
                                    <p>{{ imgUploaded ? 'Creative Uploaded! Drag & drop to replace' : 'Drag and drop to upload' }}</p>
                                    <button type="button">
                                        <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.24399 6.78613L8.11899 3.91113V13.2111C8.11511 13.3228 8.13328 13.4342 8.17244 13.5389C8.2116 13.6435 8.271 13.7395 8.34723 13.8212C8.42347 13.9029 8.51505 13.9688 8.61676 14.0151C8.71847 14.0614 8.8283 14.0873 8.93999 14.0911H8.99499C9.10668 14.095 9.21804 14.0768 9.32271 14.0377C9.42738 13.9985 9.52331 13.9391 9.60503 13.8629C9.68674 13.7867 9.75264 13.6951 9.79896 13.5934C9.84528 13.4917 9.87112 13.3818 9.87499 13.2701V3.91113L12.725 6.75613C12.8853 6.92201 13.1044 7.01806 13.335 7.02349C13.5656 7.02892 13.7891 6.94328 13.957 6.78513L13.986 6.75613C14.1415 6.58872 14.228 6.36866 14.228 6.14013C14.228 5.91161 14.1415 5.69155 13.986 5.52413L9.61499 1.15313C9.44758 0.997586 9.22751 0.911133 8.99899 0.911133C8.77047 0.911133 8.5504 0.997586 8.38299 1.15313L4.01199 5.52413C3.85644 5.69155 3.76999 5.91161 3.76999 6.14013C3.76999 6.36866 3.85644 6.58872 4.01199 6.75613C4.17213 6.92215 4.39124 7.01838 4.62183 7.024C4.85243 7.02962 5.07595 6.94416 5.24399 6.78613Z" fill="white"/>
                                            <path d="M16.539 9.78225C16.4273 9.77837 16.3159 9.79654 16.2113 9.8357C16.1066 9.87486 16.0107 9.93425 15.9289 10.0105C15.8472 10.0867 15.7813 10.1783 15.735 10.28C15.6887 10.3817 15.6629 10.4916 15.659 10.6032V14.5632C15.6538 14.6393 15.6213 14.7108 15.5674 14.7647C15.5136 14.8186 15.442 14.8511 15.366 14.8563H2.63399C2.55797 14.8511 2.4864 14.8186 2.43253 14.7647C2.37866 14.7108 2.34613 14.6393 2.34099 14.5632V10.6633C2.34486 10.5516 2.3267 10.4402 2.28754 10.3355C2.24837 10.2309 2.18898 10.1349 2.11274 10.0532C2.03651 9.9715 1.94492 9.9056 1.84321 9.85928C1.74151 9.81296 1.63168 9.78712 1.51999 9.78325H1.45999C1.34383 9.78108 1.22842 9.80236 1.12067 9.84581C1.01292 9.88927 0.915046 9.95401 0.832894 10.0362C0.750741 10.1183 0.686003 10.2162 0.642548 10.3239C0.599093 10.4317 0.577813 10.5471 0.579985 10.6633V14.5632C0.596298 15.1028 0.817939 15.6159 1.19966 15.9976C1.58138 16.3793 2.0944 16.6009 2.63399 16.6172H15.366C15.9056 16.6009 16.4186 16.3793 16.8003 15.9976C17.182 15.6159 17.4037 15.1028 17.42 14.5632V10.6633C17.4223 10.5469 17.4011 10.4313 17.3576 10.3234C17.3142 10.2155 17.2494 10.1174 17.1671 10.0351C17.0848 9.95286 16.9868 9.88806 16.8788 9.8446C16.7709 9.80115 16.6553 9.77994 16.539 9.78225Z" fill="white"/>
                                        </svg>
                                        {{ imgUploaded ? 'Upload Another Creative' : 'Upload Creative' }}
                                    </button>
                                    <input type="file" @change="imageUploaded" style="opacity: 0; width: 100%; height: 100%; position: absolute; top:0; left:0; cursor: pointer;" accept=".mp4, .jpeg, .jpg, .png, .gif, .flv, .mov, .avi" />
                                    <label class="error" v-if="errors && errors.image && !imgUploaded">{{ errors.image }}</label>
                                </div>

                                <div class="info__container">
                                    <div class="info-card">
                                        <strong>Static Image ($4 CPM)</strong>
                                        <span>JPEG, PNG, GIF</span>
                                        <span>Size limit 10MB</span>
                                    </div>
                                    <div class="info-card">
                                        <strong>Video ($7 CPM)</strong>
                                        <span>MP4, MOV, AVI, FLV</span>
                                        <span>Size limit 25MB · Max 30s</span>
                                    </div>
                                </div>

                                <div class="slider__container">
                                    <label class="form-label mb-1">Ad Playback Duration: <strong>{{ campaign.duration }}s</strong></label>
                                    <div class="d-flex align-items-center">
                                        <span class="mr-2">8s</span>
                                        <vue-slider v-model="campaign.duration" :min="8" :max="30" @change="durationChanged" style="flex: 1;" />
                                        <span class="ml-2">30s</span>
                                    </div>
                                </div>
                            </div>

                            <div class="preview__holder">
                                <div class="preview-wrap">
                                    <img src="./../../assets/adway_demo_car.png" alt="Demo Car" />
                                    <img 
                                        class="overflow__img" 
                                        ref="overflowIMG" 
                                        alt="Preview Image" 
                                        v-show="hasPreviewImage"
                                    />
                                    <video id="videoPreviewImg" style="display: none; opacity: 0"></video>
                                    <canvas 
                                        id="prevImgCanvas" 
                                        v-show="imgUploaded && creativeType === 'video'"
                                    ></canvas>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-card__actions" v-if="currentStep === 4">
                        <button type="button" class="btn-back" @click="goToStep(3)">← Back</button>
                        <button type="button" class="btn-continue" @click="nextStep(4)">Continue to Budget & Estimates →</button>
                    </div>
                </div>
            </div>

            <!-- ================= STEP 5: COST & BUDGET CALCULATION ================= -->
            <div 
                id="step-5" 
                class="step-card"
                :class="getStepClass(5)"
            >
                <div class="step-card__header" @click="maxStepReached >= 5 && goToStep(5)">
                    <div class="step-card__badge">5</div>
                    <div class="step-card__header-info">
                        <h2>Cost & Budget Calculation</h2>
                        <p>Set budget in $$$ or target impressions with instant live estimate.</p>
                    </div>
                    <div class="step-card__status">
                        <span v-if="currentStep === 5" class="status-pill status-pill--active">In Progress</span>
                        <span v-else-if="currentStep > 5" class="status-pill status-pill--done">Completed (Edit)</span>
                        <span v-else class="status-pill status-pill--locked">Upcoming</span>
                    </div>
                </div>

                <div v-show="currentStep === 5 || currentStep > 5" class="step-card__content">
                    <!-- Pricing Structure Bar -->
                    <div class="pricing-rate-card">
                        <div class="rate-item" :class="{'rate-item--selected': creativeType === 'image'}">
                            <span class="rate-type">🖼️ Static Image</span>
                            <span class="rate-cost">$4.00 / 1,000 impressions</span>
                            <span class="rate-tag" v-if="creativeType === 'image'">Active Rate</span>
                        </div>
                        <div class="rate-item" :class="{'rate-item--selected': creativeType === 'video'}">
                            <span class="rate-type">🎥 Video Creative</span>
                            <span class="rate-cost">$7.00 / 1,000 impressions</span>
                            <span class="rate-tag" v-if="creativeType === 'video'">Active Rate</span>
                        </div>
                    </div>

                    <!-- Mode Toggle & Quick Budget Suggestions -->
                    <div class="budget-control-section mt-4">
                        <div class="budget-mode-tabs">
                            <button 
                                type="button" 
                                class="budget-tab" 
                                :class="{'budget-tab--active': budgetMode === 'budget'}"
                                @click="setBudgetMode('budget')"
                            >
                                Set by Budget ($)
                            </button>
                            <button 
                                type="button" 
                                class="budget-tab" 
                                :class="{'budget-tab--active': budgetMode === 'impressions'}"
                                @click="setBudgetMode('impressions')"
                            >
                                Set by Impressions
                            </button>
                        </div>

                        <!-- Budget in $$$ Mode -->
                        <div v-if="budgetMode === 'budget'" class="budget-input-box">
                            <div class="suggested-pills-row">
                                <span class="helper-label">Suggested Starting Budgets:</span>
                                <button type="button" class="helper-btn" :class="{'helper-btn--active': total_budget === 500}" @click="onPresetBudgetClick(500)">$500</button>
                                <button type="button" class="helper-btn" :class="{'helper-btn--active': total_budget === 1000}" @click="onPresetBudgetClick(1000)">$1,000 (Suggested)</button>
                                <button type="button" class="helper-btn" :class="{'helper-btn--active': total_budget === 2500}" @click="onPresetBudgetClick(2500)">$2,500</button>
                                <button type="button" class="helper-btn" :class="{'helper-btn--active': total_budget === 5000}" @click="onPresetBudgetClick(5000)">$5,000</button>
                            </div>

                            <div class="slider-typed-wrap">
                                <div class="typed-input-wrap">
                                    <span class="currency-symbol">$</span>
                                    <input 
                                        type="number" 
                                        class="typed-budget-input" 
                                        v-model.number="typedBudget" 
                                        @input="onTypedBudgetInput"
                                        min="100" 
                                        step="50"
                                    />
                                </div>
                                <div class="slider-flex">
                                    <vue-slider 
                                        v-model="sliderBudgetValue" 
                                        :min="200" 
                                        :max="10000" 
                                        :interval="50" 
                                        @change="onSliderBudgetChange"
                                    />
                                    <div class="slider-scale">
                                        <span>$200</span>
                                        <span>$5,000</span>
                                        <span>$10,000+</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Impressions Mode -->
                        <div v-else class="budget-input-box">
                            <div class="slider-typed-wrap">
                                <div class="typed-input-wrap">
                                    <span class="currency-symbol">👁️</span>
                                    <input 
                                        type="number" 
                                        class="typed-budget-input" 
                                        v-model.number="typedImpressions" 
                                        @input="onTypedImpressionsInput"
                                        min="10000" 
                                        step="5000"
                                    />
                                </div>
                                <div class="slider-flex">
                                    <vue-slider 
                                        v-model="sliderImpressionsValue" 
                                        :min="25000" 
                                        :max="2500000" 
                                        :interval="5000" 
                                        @change="onSliderImpressionsChange"
                                    />
                                    <div class="slider-scale">
                                        <span>25k</span>
                                        <span>1.25M</span>
                                        <span>2.5M</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Live Immediate Estimate Box -->
                    <div class="campaign-estimate-card mt-4">
                        <div class="estimate-icon">✨</div>
                        <div class="estimate-content">
                            <div class="estimate-title">Your campaign estimate</div>
                            <div class="estimate-body">
                                <strong>{{ selectedCity || 'Los Angeles' }}</strong> · 
                                <span>{{ formattedDateRange }}</span> · 
                                <span>{{ creativeType === 'video' ? 'Video' : 'Static Image' }}</span> · 
                                <span>Budget: <strong>${{ Number(total_budget).toLocaleString() }}</strong></span> · 
                                <span>Estimated impressions: <strong>{{ Number(total_impressions).toLocaleString() }}</strong></span>
                            </div>
                            <div class="estimate-footer">
                                🚗 Runs across available Adway vehicles in your selected area.
                            </div>
                        </div>
                    </div>

                    <div class="step-card__actions" v-if="currentStep === 5">
                        <button type="button" class="btn-back" @click="goToStep(4)">← Back</button>
                        <button type="button" class="btn-continue" @click="nextStep(5)">Review & Confirm Order →</button>
                    </div>
                </div>
            </div>

            <!-- ================= STEP 6: SUMMARY, CONFIRMATION & STRIPE CHECKOUT ================= -->
            <div 
                id="step-6" 
                class="step-card"
                :class="getStepClass(6)"
            >
                <div class="step-card__header" @click="maxStepReached >= 6 && goToStep(6)">
                    <div class="step-card__badge">6</div>
                    <div class="step-card__header-info">
                        <h2>Summary and Confirmation</h2>
                        <p>Review final order details, approval schedule, and proceed to payment.</p>
                    </div>
                    <div class="step-card__status">
                        <span v-if="currentStep === 6" class="status-pill status-pill--active">Final Step</span>
                        <span v-else class="status-pill status-pill--locked">Upcoming</span>
                    </div>
                </div>

                <div v-show="currentStep === 6" class="step-card__content">
                    <div class="order-summary-card">
                        <div class="summary-top-header">
                            <div>
                                <h3 class="summary-campaign-name">Campaign: {{ campaign.name || 'Unnamed Campaign' }}</h3>
                                <p class="summary-client" v-if="campaign.client">Client: {{ campaign.client }}</p>
                            </div>
                            <div class="summary-total-price">
                                <span class="total-label">Final Campaign Price</span>
                                <span class="total-number">${{ Number(total_budget).toLocaleString() }}</span>
                            </div>
                        </div>

                        <div class="summary-grid">
                            <div class="summary-item">
                                <span class="summary-item-label">📅 Flight Schedule</span>
                                <span class="summary-item-val">{{ formattedFlightSummary }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-item-label">⏰ Broadcast Timings</span>
                                <span class="summary-item-val">{{ campaign.start_time || '18:00' }} to {{ campaign.end_time || '06:00' }} daily</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-item-label">📍 Target Area</span>
                                <span class="summary-item-val">{{ selectedCity }} {{ showPreciseGeofencing ? '(Custom Geofence Enabled)' : '' }}</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-item-label">🎨 Creative & Format</span>
                                <span class="summary-item-val">{{ creativeType === 'video' ? 'Video Asset' : 'Static Image' }} ({{ campaign.duration }}s)</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-item-label">👁️ Estimated Impressions</span>
                                <span class="summary-item-val"><strong>{{ Number(total_impressions).toLocaleString() }}</strong> impressions</span>
                            </div>
                            <div class="summary-item">
                                <span class="summary-item-label">🏷️ Rate</span>
                                <span class="summary-item-val">${{ currentCPM }}.00 CPM</span>
                            </div>
                        </div>

                        <!-- Line Items & Production Charge -->
                        <div class="line-items-table">
                            <div class="line-item line-item--total">
                                <span>Order Total Due Today</span>
                                <span>${{ Number(total_budget).toLocaleString() }} USD</span>
                            </div>
                        </div>

                        <!-- Policy / Creative Review Notice -->
                        <div class="policy-notice-box">
                            <div class="notice-icon">ℹ️</div>
                            <div class="notice-text">
                                <strong>Creative Review & Approval Notice:</strong>
                                All submitted creatives undergo mandatory quality and compliance review prior to broadcast.
                                Your campaign is scheduled to begin running on <strong>{{ formattedStartDate }}</strong> once creative review is completed and approved.
                            </div>
                        </div>
                    </div>

                    <!-- Final Action Buttons -->
                    <div class="step-card__actions mt-4">
                        <button type="button" class="btn-back" @click="goToStep(5)" :disabled="activeRequest">← Back to Budget</button>
                        
                        <!-- Primary Stripe Checkout Button -->
                        <button 
                            type="button" 
                            class="btn-checkout" 
                            @click="handleFinalSubmit('stripe')" 
                            :disabled="activeRequest"
                        >
                            <clip-loader color="#ffffff" size="18px" :loading="activeRequest" />
                            <span v-if="!activeRequest">💳 Pay ${{ Number(total_budget).toLocaleString() }} with Stripe Checkout</span>
                            <span v-else>{{ paymentLoadingText }}</span>
                        </button>

                        <!-- Save Campaign (Pay Later in Finance) -->
                        <button 
                            type="button" 
                            class="btn-secondary-save" 
                            @click="handleFinalSubmit('save_only')"
                            :disabled="activeRequest"
                        >
                            💾 Save Campaign (Pay Later)
                        </button>
                    </div>
                </div>
            </div>

            <!-- Sticky Summary Bar at bottom of screen -->
            <div class="sticky-footer-summary" v-if="currentStep >= 4">
                <div class="sticky-left">
                    <div class="sticky-metric">
                        <span class="metric-label">TOTAL BUDGET</span>
                        <span class="metric-val">${{ Number(total_budget).toLocaleString() }}</span>
                    </div>
                    <div class="sticky-metric">
                        <span class="metric-label">ESTIMATED IMPRESSIONS</span>
                        <span class="metric-val">{{ Number(total_impressions).toLocaleString() }} (@ ${{ currentCPM }} CPM)</span>
                    </div>
                </div>
                <div class="sticky-right">
                    <button type="button" class="btn-footer-cancel" @click="cancelCampaign()">Cancel</button>
                    <button 
                        type="button" 
                        class="btn-footer-action" 
                        @click="currentStep === 6 ? handleFinalSubmit('stripe') : nextStep(currentStep)"
                        :disabled="activeRequest"
                    >
                        {{ currentStep === 6 ? (activeRequest ? paymentLoadingText : `Pay $${Number(total_budget).toLocaleString()}`) : 'Continue Step →' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SideMenuHeader from './../design/SideMenuHeader.vue'
import SmallCar from '@components/svg/SmallCar.vue'
import MediumCar from '@components/svg/MediumCar.vue'
import LuxuryCar from '@components/svg/LuxuryCar.vue'
import Truck from '@components/svg/Truck.vue'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import TimePicker from '@components/partials/TimePicker.vue'
import XLSX from 'xlsx'
import styles from '@utils/mapStylesDark.js'
import { gmapApi } from 'vue2-google-maps'
import { reverseGeocode, InitZoomControl, InitTypeControl, InitTrafficVolume, InitResizeIcons } from '@utils/MapsHelper.js'
import Datepicker from 'vuejs-datepicker'
import { mapGetters } from 'vuex'
import toast from '@utils/toast'
import ClipLoader from 'vue-spinner/src/ClipLoader.vue'
import GeofenceIcon from '@svg/Geofence'
import ZipcodeIcon from '@svg/Zipcode'
import moment from 'moment'
import store from "@store"

let geocoder = null

const CITIES = {
    'Los Angeles': { lat: 34.0522, lon: -118.2437, address: 'Los Angeles, CA, USA', distance: 50000 },
    'Phoenix': { lat: 33.4484, lon: -112.0740, address: 'Phoenix, AZ, USA', distance: 50000 },
    'San Diego': { lat: 32.7157, lon: -117.1611, address: 'San Diego, CA, USA', distance: 45000 },
    'San Francisco': { lat: 37.7749, lon: -122.4194, address: 'San Francisco, CA, USA', distance: 40000 },
    'Other TBD': { lat: 34.0522, lon: -118.2437, address: 'Other TBD - Custom Market', distance: 40000 }
}

export default {
    data () {
        return {
            store,
            // Stepper state
            currentStep: 1,
            maxStepReached: 1,
            steps: [
                { number: 1, title: 'Details' },
                { number: 2, title: 'Where & When' },
                { number: 3, title: 'Location' },
                { number: 4, title: 'Creative' },
                { number: 5, title: 'Budget' },
                { number: 6, title: 'Confirmation' }
            ],

            // Step 1: Vehicle targeting collapsible
            showAdvancedVehicle: false,

            // Step 2: Date & Timing helpers
            timingPreset: 'night',

            // Step 3: Location
            selectedCity: 'Los Angeles',
            cityOptions: ['Los Angeles', 'Phoenix', 'San Diego', 'San Francisco', 'Other TBD'],
            showPreciseGeofencing: false,

            // Step 4: Creative & Pricing
            creativeType: 'image', // 'image' or 'video'

            // Step 5: Budget & Impressions
            budgetMode: 'budget', // 'budget' or 'impressions'
            typedBudget: 1000,
            sliderBudgetValue: 1000,
            typedImpressions: 250000,
            sliderImpressionsValue: 250000,

            // Core campaign data model (preserved for backend compatibility)
            campaignCategories: [],
            allCategories: false,
            initialCampaign: {},
            campaign: {
                name: null,
                client: null,
                category: null,
                lat: 34.0522,
                lon: -118.2437,
                address: 'Los Angeles, CA, USA',
                distance: 50000,
                duration: 15,
                start_date: null,
                end_date: null,
                start_time: '18:00',
                end_time: '06:00',
                spend_limit: 1000,
                show_limit: 250000,
                car_types: [1, 2, 3, 4],
                device_group: '',
                ZIP_codes: [],
                active: 'Y'
            },
            zipCode: null,
            drag: false,
            styles,
            polygons: [],
            map: null,
            content: null,
            mapTypeActive: 'geofence',
            imgUploaded: false,
            trafficVolume: {
                minPeople: '',
                maxPeople: '',
                avgPeople: ''
            },
            campaigns: null,
            errors: {},
            total_budget: 1000,
            total_impressions: 250000,
            activeRequest: false,
            paymentLoadingText: 'Preparing Checkout...',
            isProcessingPayment: false,
            lastCenter: { lat: null, lng: null },
            bounds: null,
            CPM: 4
        }
    },

    computed: {
        google: gmapApi,
        isEditMode () {
            return !!this.$route.params.id
        },
        currentCPM () {
            return this.creativeType === 'video' ? 7 : 4
        },
        formattedStartDate () {
            if (!this.campaign.start_date) return 'TBD'
            return moment(this.campaign.start_date).format('MMMM D, YYYY')
        },
        formattedDateRange () {
            if (!this.campaign.start_date || !this.campaign.end_date) {
                return 'Upcoming Flight'
            }
            const start = moment(this.campaign.start_date)
            const end = moment(this.campaign.end_date)
            if (start.format('YYYY-MM') === end.format('YYYY-MM')) {
                return `${start.format('MMMM D')}–${end.format('D')}`
            } else if (start.format('YYYY') === end.format('YYYY')) {
                return `${start.format('MMM D')} – ${end.format('MMM D')}`
            } else {
                return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`
            }
        },
        formattedFlightSummary () {
            if (!this.campaign.start_date || !this.campaign.end_date) return 'Not scheduled yet'
            return `${moment(this.campaign.start_date).format('MMM D, YYYY')} to ${moment(this.campaign.end_date).format('MMM D, YYYY')}`
        },
        hasPreviewImage () {
            return (this.imgUploaded && this.creativeType === 'image') || (!this.imgUploaded && !!this.campaign.content_url && this.creativeType !== 'video')
        },
        ...mapGetters({
            schema: 'getSchema',
            user: 'getUser',
            token: 'getToken'
        })
    },

    methods: {
        // --- STEPPER NAVIGATION ---
        getStepClass (step) {
            if (step === this.currentStep) return 'step-card--active'
            if (step < this.currentStep) return 'step-card--completed'
            return 'step-card--dimmed'
        },
        handleStepClick (step) {
            if (this.maxStepReached >= step) {
                this.goToStep(step)
            }
        },
        goToStep (step) {
            this.currentStep = step
            this.$nextTick(() => {
                const el = document.getElementById(`step-${step}`)
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
            })
        },
        nextStep (stepNumber) {
            if (!this.validateStep(stepNumber)) {
                return
            }
            const next = stepNumber + 1
            this.currentStep = next
            if (next > this.maxStepReached) {
                this.maxStepReached = next
            }
            this.goToStep(next)
        },

        // --- STEP VALIDATIONS ---
        validateStep (step) {
            this.errors = {}
            let valid = true

            if (step === 1) {
                if (!this.campaign.name || !this.campaign.name.trim()) {
                    this.errors.name = 'Please enter a campaign name.'
                    valid = false
                }
                if (!this.campaign.client && this.user.role !== 'advertiser') {
                    this.errors.client = 'Please enter a client name.'
                    valid = false
                }
            } else if (step === 2) {
                if (!this.campaign.start_date || !this.campaign.end_date) {
                    this.errors.time = 'Please select both start and end dates.'
                    valid = false
                    toast.errorMessage('Please select campaign start and end dates.')
                } else if (new Date(this.campaign.start_date) > new Date(this.campaign.end_date)) {
                    this.errors.time = 'End date cannot be earlier than start date.'
                    valid = false
                    toast.errorMessage(this.errors.time)
                }
                if (!this.campaign.start_time || !this.campaign.end_time) {
                    this.errors.time = 'Please set daily start and end timings.'
                    valid = false
                }
            } else if (step === 3) {
                if (this.showPreciseGeofencing) {
                    if (!this.campaign.address && this.mapTypeActive === 'geofence') {
                        this.errors.address = 'Please select or click an address on the map.'
                        valid = false
                    }
                    if (this.campaign.ZIP_codes.length === 0 && this.mapTypeActive === 'zipcode') {
                        this.errors.zip = 'Please select at least one zipcode.'
                        valid = false
                    }
                }
            } else if (step === 4) {
                if (!this.imgUploaded && !this.campaign.content_url) {
                    this.errors.image = 'Please upload a static image or video creative.'
                    valid = false
                    toast.errorMessage(this.errors.image)
                }
            } else if (step === 5) {
                if (!this.total_budget || this.total_budget <= 0) {
                    valid = false
                    toast.errorMessage('Please specify a valid budget.')
                }
            }

            return valid
        },

        // --- STEP 2: DATE & TIMING HELPERS ---
        applyDatePreset (type) {
            const now = new Date()
            if (type === 'next_month') {
                const start = new Date(now.getFullYear(), now.getMonth() + 1, 1)
                const end = new Date(now.getFullYear(), now.getMonth() + 2, 0)
                this.campaign.start_date = start
                this.campaign.end_date = end
            } else if (type === 'one_month') {
                const start = this.campaign.start_date ? new Date(this.campaign.start_date) : now
                this.campaign.start_date = start
                const end = new Date(start)
                end.setMonth(end.getMonth() + 1)
                this.campaign.end_date = end
            } else if (type === 'two_weeks') {
                const start = this.campaign.start_date ? new Date(this.campaign.start_date) : now
                this.campaign.start_date = start
                const end = new Date(start)
                end.setDate(end.getDate() + 14)
                this.campaign.end_date = end
            } else if (type === 'three_months') {
                const start = this.campaign.start_date ? new Date(this.campaign.start_date) : now
                this.campaign.start_date = start
                const end = new Date(start)
                end.setMonth(end.getMonth() + 3)
                this.campaign.end_date = end
            }
        },

        applyTimingPreset (preset) {
            this.timingPreset = preset
            if (preset === 'night') {
                this.campaign.start_time = '18:00'
                this.campaign.end_time = '06:00'
            } else if (preset === '24/7' || preset === 'allday') {
                this.campaign.start_time = '00:00'
                this.campaign.end_time = '23:59'
            } else if (preset === 'evening') {
                this.campaign.start_time = '16:00'
                this.campaign.end_time = '20:00'
            }
        },

        onCustomTimeChange () {
            this.timingPreset = 'custom'
        },

        // --- STEP 3: LOCATION SELECTION ---
        onCitySelected (cityName) {
            this.selectedCity = cityName
            const cityData = CITIES[cityName]
            if (cityData) {
                this.campaign.lat = cityData.lat
                this.campaign.lon = cityData.lon
                this.campaign.address = cityData.address
                this.campaign.distance = cityData.distance

                if (this.map) {
                    this.map.panTo({ lat: cityData.lat, lng: cityData.lon })
                    this.map.setZoom(10)
                }
                this.getTrafficVolume()
            }
        },

        // --- STEP 5: BUDGET & PRICING CALCULATIONS ---
        setBudgetMode (mode) {
            this.budgetMode = mode
            this.recalculateFromActiveMode()
        },

        onPresetBudgetClick (amount) {
            this.budgetMode = 'budget'
            this.typedBudget = amount
            this.sliderBudgetValue = amount
            this.recalculateFromBudget(amount)
        },

        onTypedBudgetInput () {
            const val = Math.max(0, Number(this.typedBudget) || 0)
            this.sliderBudgetValue = Math.min(10000, val)
            this.recalculateFromBudget(val)
        },

        onSliderBudgetChange (val) {
            this.typedBudget = val
            this.recalculateFromBudget(val)
        },

        recalculateFromBudget (budgetAmount) {
            this.total_budget = Math.round(budgetAmount)
            this.total_impressions = Math.round((this.total_budget / this.currentCPM) * 1000)
            this.campaign.spend_limit = this.total_budget
            this.campaign.show_limit = this.total_impressions
            this.typedImpressions = this.total_impressions
            this.sliderImpressionsValue = Math.min(2500000, this.total_impressions)
            this.CPM = this.currentCPM
        },

        onTypedImpressionsInput () {
            const val = Math.max(0, Number(this.typedImpressions) || 0)
            this.sliderImpressionsValue = Math.min(2500000, val)
            this.recalculateFromImpressions(val)
        },

        onSliderImpressionsChange (val) {
            this.typedImpressions = val
            this.recalculateFromImpressions(val)
        },

        recalculateFromImpressions (impressionsAmount) {
            this.total_impressions = Math.round(impressionsAmount)
            this.total_budget = Math.round((this.total_impressions / 1000) * this.currentCPM)
            this.campaign.spend_limit = this.total_budget
            this.campaign.show_limit = this.total_impressions
            this.typedBudget = this.total_budget
            this.sliderBudgetValue = Math.min(10000, this.total_budget)
            this.CPM = this.currentCPM
        },

        recalculateFromActiveMode () {
            if (this.budgetMode === 'budget') {
                this.recalculateFromBudget(this.typedBudget)
            } else {
                this.recalculateFromImpressions(this.typedImpressions)
            }
        },

        // --- SUBMISSION & STRIPE CHECKOUT ---
        handleFinalSubmit (mode = 'stripe') {
            for (let step = 1; step <= 5; step++) {
                if (!this.validateStep(step)) {
                    this.goToStep(step)
                    return
                }
            }

            if (mode === 'save_only') {
                this.activeRequest = true
                this.paymentLoadingText = 'Saving campaign...'
                this.saveCampaignToBackend((savedCampaignId) => {
                    this.activeRequest = false
                    toast.successMessage('Campaign saved successfully! You can fund it in Finance.')
                    this.$router.push({ name: 'Dashboard' })
                })
                return
            }

            // mode === 'stripe'
            this.startStripeCheckout()
        },

        startStripeCheckout () {
            const stripeKey = (this.store && this.store.getters && this.store.getters.keys && this.store.getters.keys.stripeKey) || ''
            if (!stripeKey) {
                toast.errorMessage('Stripe configuration key is missing.')
                return
            }

            if (!window.StripeCheckout) {
                this.activeRequest = true
                this.paymentLoadingText = 'Loading Stripe Checkout...'
                this.loadStripeScript(() => {
                    this.openStripeModal(stripeKey)
                }, () => {
                    this.activeRequest = false
                    toast.errorMessage('Unable to load Stripe Checkout. You can save your campaign and pay via Finance.')
                })
                return
            }

            this.openStripeModal(stripeKey)
        },

        loadStripeScript (onSuccess, onError) {
            if (window.StripeCheckout) {
                onSuccess()
                return
            }
            if (!window.Stripe) {
                const v2 = document.createElement('script')
                v2.src = 'https://js.stripe.com/v2/'
                document.head.appendChild(v2)
            }
            const script = document.createElement('script')
            script.src = 'https://checkout.stripe.com/checkout.js'
            script.onload = () => {
                if (window.StripeCheckout) {
                    onSuccess()
                } else {
                    onError && onError()
                }
            }
            script.onerror = () => {
                onError && onError()
            }
            document.head.appendChild(script)
        },

        openStripeModal (stripeKey) {
            this.activeRequest = true
            this.paymentLoadingText = 'Opening Stripe Checkout...'
            let checkoutOpened = false

            try {
                const amountInCents = Math.round(this.total_budget * 100)
                const handler = window.StripeCheckout.configure({
                    key: stripeKey,
                    image: 'https://stripe.com/img/documentation/checkout/marketplace.png',
                    locale: 'auto',
                    opened: () => {
                        checkoutOpened = true
                        this.paymentLoadingText = 'Waiting for payment details...'
                    },
                    closed: () => {
                        if (!this.isProcessingPayment) {
                            this.activeRequest = false
                        }
                    },
                    token: (stripeToken) => {
                        this.isProcessingPayment = true
                        this.paymentLoadingText = 'Saving campaign & processing payment...'
                        this.activeRequest = true
                        this.saveCampaignToBackend((savedCampaignId) => {
                            this.processStripePayment(stripeToken, savedCampaignId)
                        })
                    }
                })

                handler.open({
                    name: 'Adway Fleet Advertising',
                    description: `${this.campaign.name} (${Number(this.total_impressions).toLocaleString()} impressions)`,
                    amount: amountInCents,
                    currency: 'usd',
                    email: (this.user && this.user.email) || ''
                })

                // Safety timeout: If modal didn't open in 4s and payment isn't in flight, release activeRequest
                setTimeout(() => {
                    if (this.activeRequest && !checkoutOpened && !this.isProcessingPayment) {
                        this.activeRequest = false
                        toast.errorMessage('Could not open Stripe Checkout window. Please check your browser popup blocker or use Save Campaign.')
                    }
                }, 4000)
            } catch (err) {
                this.activeRequest = false
                this.isProcessingPayment = false
                console.error('Error opening Stripe Checkout:', err)
                toast.errorMessage('Failed to open Stripe Checkout: ' + (err.message || err))
            }
        },

        saveCampaignToBackend (onSuccess) {
            const url = '/set/campaign'
            const formData = new FormData()

            const formattedStartDate = this.customFormatter(this.campaign.start_date)
            const formattedEndDate = this.customFormatter(this.campaign.end_date)

            let startTimeStr = this.campaign.start_time
            if (startTimeStr && startTimeStr.toString().length > 5) {
                startTimeStr = moment(startTimeStr).format('HH:mm')
            }
            let endTimeStr = this.campaign.end_time
            if (endTimeStr && endTimeStr.toString().length > 5) {
                endTimeStr = moment(endTimeStr).format('HH:mm')
            }

            const data = {
                ...this.campaign,
                start_date: formattedStartDate,
                end_date: formattedEndDate,
                start_time: startTimeStr,
                end_time: endTimeStr,
                spend_limit: this.total_budget,
                show_limit: this.total_impressions,
                device_group: this.sanitizeDeviceGroup(this.campaign.device_group)
            }

            const json = {
                data,
                API_KEY: this.store.getters.keys.api_key,
                code: this.token.token,
                user: this.user.user
            }

            formData.append('json', JSON.stringify(json))
            if (this.content) {
                formData.append('content', this.content)
            } else {
                formData.append('content', '')
            }

            this.request(url, 'POST', formData).then((response) => {
                if (response.code === 112) {
                    this.checkTokenConditions(() => this.saveCampaignToBackend(onSuccess))
                    return
                }
                const isSuccess = response.code === 0 || response.result === "success"
                if (isSuccess) {
                    const campaignId = (response.data && response.data[0] && response.data[0].campaign_id) || response.campaign_id || this.campaign.campaign_id || this.$route.params.id
                    onSuccess(campaignId)
                } else {
                    this.activeRequest = false
                    this.isProcessingPayment = false
                    toast.failedInsert(response.message || 'Failed to save campaign')
                }
            }).catch((error) => {
                this.activeRequest = false
                this.isProcessingPayment = false
                toast.failedInsert(error.message || error)
            })
        },

        processStripePayment (stripeToken, campaignId) {
            const formData = new FormData()
            const json = {
                data: {
                    amount: this.total_budget,
                    token: stripeToken.id,
                    campaign_id: campaignId
                },
                API_KEY: this.store.getters.keys.api_key,
                code: this.token.token,
                user: this.user.user
            }
            formData.append('json', JSON.stringify(json))

            this.request('/make/payment', 'POST', formData).then((res) => {
                if (res && res.code === 112) {
                    this.checkTokenConditions(() => this.processStripePayment(stripeToken, campaignId))
                    return
                }

                this.activeRequest = false
                this.isProcessingPayment = false
                if (res && (res.code === 0 || res.result === 'success')) {
                    toast.successMessage('Payment successful! Campaign submitted for review.')
                    this.sendCampaignPaidNotification({
                        campaignId,
                        campaignName: this.campaign.name,
                        client: this.campaign.client,
                        userEmail: this.user.email,
                        userName: this.user.user,
                        totalBudget: this.total_budget,
                        totalImpressions: this.total_impressions,
                        cpm: this.currentCPM,
                        creativeType: this.creativeType,
                        flightDates: this.formattedFlightSummary,
                        timings: `${this.campaign.start_time} - ${this.campaign.end_time}`,
                        location: this.selectedCity
                    })
                    this.$router.push({ name: 'Dashboard' })
                } else {
                    toast.errorMessage((res && res.message) || 'Payment processing error')
                }
            }).catch((err) => {
                this.activeRequest = false
                this.isProcessingPayment = false
                toast.errorMessage((err && err.message) || 'Payment failed')
            })
        },

        // --- EMAIL NOTIFICATION STUB (Prepared for future implementation) ---
        sendCampaignPaidNotification (orderData) {
            console.log('[Notification Prepared] Sending email notification to user and admin:', orderData)
            /*
            this.request('/notify/campaign/paid', 'POST', {
                campaign_id: orderData.campaignId,
                recipient_user: orderData.userEmail,
                admin_notification: true,
                summary: orderData
            })
            */
        },

        // --- MEDIA HANDLING & CAR VISUALIZATION ---
        imageUploaded (event) {
            const reader = new FileReader()
            const self = this
            this.content = event.target.files[0]
            if (!this.content) return

            const extension = this.content.type
            const checkVideo = extension.includes("video")
            this.creativeType = checkVideo ? 'video' : 'image'
            this.recalculateFromActiveMode()

            const fileSize = this.content.size / 1024 / 1024
            if (fileSize > 25 && checkVideo) {
                event.preventDefault()
                toast.errorMessage("Maximum allowed video size is 25MB!")
                return
            }
            if (fileSize > 10 && !checkVideo) {
                event.preventDefault()
                toast.errorMessage("Maximum allowed image size is 10MB!")
                return
            }

            reader.onload = function (e) {
                if (checkVideo) {
                    const canvas = document.getElementById("prevImgCanvas")
                    const dragcanvas = document.getElementById("dragImgCanvas")
                    if (canvas && dragcanvas) {
                        canvas.width = dragcanvas.width = '150'
                        canvas.height = dragcanvas.height = '100'
                    }
                    const video = document.getElementById("videoPreviewImg")
                    if (video) {
                        video.addEventListener('loadeddata', function () {
                            self.clearCanvas()
                            self.reloadRandomFrame()
                        }, false)

                        video.addEventListener('seeked', function () {
                            const v = document.getElementById("videoPreviewImg")
                            if (canvas && dragcanvas && v) {
                                const context = canvas.getContext('2d')
                                context.drawImage(v, 0, 0, canvas.width, canvas.height)
                                dragcanvas.getContext('2d').drawImage(canvas, 0, 0)
                            }
                        }, false)

                        video.src = e.target.result
                    }
                    if (canvas) canvas.style.opacity = 1
                    if (dragcanvas) dragcanvas.style.opacity = 1
                    if (self.$refs.overflowIMG) {
                        self.$refs.overflowIMG.src = ''
                        self.$refs.overflowIMG.style.opacity = 0
                    }
                    if (self.$refs.dragIMG) self.$refs.dragIMG.src = ''
                    self.imgUploaded = true
                } else {
                    if (self.$refs.overflowIMG) {
                        self.$refs.overflowIMG.src = e.target.result
                        self.$refs.overflowIMG.style.opacity = 1
                    }
                    if (self.$refs.dragIMG) {
                        self.$refs.dragIMG.src = e.target.result
                    }
                    self.imgUploaded = true
                    self.clearCanvas()
                }
            }

            reader.readAsDataURL(this.content)
        },

        reloadRandomFrame () {
            const video = document.getElementById("videoPreviewImg")
            if (video && !isNaN(video.duration)) {
                const rand = Math.round(Math.random() * video.duration * 1000) + 1
                video.currentTime = rand / 1000
            }
        },

        clearCanvas () {
            const canvas = document.getElementById("prevImgCanvas")
            if (canvas) {
                const context = canvas.getContext('2d')
                context.clearRect(0, 0, canvas.width, canvas.height)
            }
            const dragcanvas = document.getElementById("dragImgCanvas")
            if (dragcanvas) {
                const context2 = dragcanvas.getContext('2d')
                context2.clearRect(0, 0, dragcanvas.width, dragcanvas.height)
            }
        },

        dragEnter () { this.drag = true },
        dragLeave () { this.drag = false },
        durationChanged (val) { this.campaign.duration = val },

        // --- MAP & TARGETING METHODS ---
        mapTypeChange (type) {
            this.mapTypeActive = type
            if (type === 'zipcode') {
                this.getBoundaries()
            }
            this.getTrafficVolume()
        },

        addressChanged (place) {
            if (!place.geometry) return
            this.campaign.address = place.formatted_address
            this.campaign.lat = place.geometry.location.lat()
            this.campaign.lon = place.geometry.location.lng()
            this.lastCenter = { lat: this.campaign.lat, lng: this.campaign.lon }
            if (this.map) {
                this.map.panTo({ lat: this.campaign.lat, lng: this.campaign.lon })
                this.map.setZoom(10)
            }
            this.getTrafficVolume()
        },

        mapClicked (event) {
            if (this.mapTypeActive !== 'geofence') return
            const lat = event.latLng.lat()
            const lng = event.latLng.lng()
            this.campaign.lat = lat
            this.campaign.lon = lng
            this.lastCenter = { lat, lng }

            if (geocoder) {
                reverseGeocode(geocoder, { lat, lng }).then(res => {
                    this.campaign.address = res.formatted_address
                    this.getTrafficVolume()
                })
            }
        },

        checkKey (event) {
            const code = event.code + ''
            if (code === 'Enter' || code === 'Comma' || code === 'Space') {
                event.preventDefault()
                this.addZIP()
            }
            if (code === 'Backspace' && (this.zipCode === '' || this.zipCode === null)) {
                this.removeZIP(this.campaign.ZIP_codes[this.campaign.ZIP_codes.length - 1])
            }
        },

        addZIP () {
            if (this.zipCode !== null && this.zipCode !== '' && this.zipCode.length > 0) {
                this.campaign.ZIP_codes.push(parseInt(this.zipCode))
                this.zipCode = null
                this.getBoundaries()
                this.getTrafficVolume()
            }
        },

        removeZIP (zip) {
            this.campaign.ZIP_codes = this.campaign.ZIP_codes.filter(z => z !== zip)
            this.getBoundaries()
            this.getTrafficVolume()
        },

        excellUploaded (event) {
            const oFile = event.target.files[0]
            const self = this
            const reader = new FileReader()

            reader.onload = function (e) {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                const result = {}
                workbook.SheetNames.forEach(function (sheetName) {
                    const roa = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 })
                    if (roa.length) result[sheetName] = roa
                })

                if (result.Sheet1) {
                    result.Sheet1.forEach(zip => {
                        if (!isNaN(zip[0]) && parseInt(zip[0]) < 99999) {
                            self.campaign.ZIP_codes.push(zip[0])
                        }
                    })
                }

                if (self.mapTypeActive === 'geofence') {
                    self.mapTypeActive = 'zipcode'
                    self.getBoundaries()
                    self.getTrafficVolume()
                }
            }
            reader.readAsArrayBuffer(oFile)
        },

        getBoundaries () {
            const url = '/get/zip_code/boundaries'
            const body = { data: { zip_codes: this.campaign.ZIP_codes } }
            this.request(url, 'POST', body).then(response => {
                if (response.code === 0) {
                    this.polygons = []
                    this.bounds = new this.google.maps.LatLngBounds()
                    response.data.forEach(item => {
                        this.extractCoordinates(JSON.parse(item.boundaries))
                    })
                    if (this.map && this.campaign.ZIP_codes.length > 0) {
                        this.map.fitBounds(this.bounds)
                    }
                }
            })
        },

        extractCoordinates (coordinates, loop = false) {
            if (typeof coordinates[0][0] === "number") {
                const polygon = []
                coordinates.forEach(pair => {
                    const latLng = new this.google.maps.LatLng(pair[1], pair[0])
                    this.bounds.extend(latLng)
                    polygon.push({ lat: pair[1], lng: pair[0] })
                })
                this.polygons.push(polygon)
            } else {
                coordinates.forEach(pair => {
                    this.extractCoordinates(pair, true)
                })
            }
        },

        getTrafficVolume () {
            const url = '/get/CPM'
            const body = { data: {} }
            if (this.mapTypeActive === 'geofence') {
                body.data.lat = this.campaign.lat
                body.data.lon = this.campaign.lon
                body.data.radius = this.campaign.distance
            } else {
                body.data.zip_codes = this.campaign.ZIP_codes
                body.from = this.campaign.start_time || null
                body.till = this.campaign.end_time || null
            }

            this.request(url, 'POST', body).then(response => {
                if (response.code === 0 && response.data && response.data[0]) {
                    this.trafficVolume.minPeople = response.data[0].Min_volume
                    this.trafficVolume.maxPeople = response.data[0].Max_volume
                    this.trafficVolume.avgPeople = response.data[0].Avg_volume
                }
            }).catch(() => {})
        },

        setCircleListeners (resizeOnInit = false) {
            const self = this
            const interval = setInterval(() => {
                if (self.$refs.mapCircle && self.$refs.mapCircle.$circleObject) {
                    const circle = self.$refs.mapCircle.$circleObject
                    if (resizeOnInit && self.map) {
                        self.map.fitBounds(circle.getBounds())
                    }
                    let dragging = false
                    self.google.maps.event.addListener(circle, 'mousedown', () => { dragging = true })
                    self.google.maps.event.addListener(circle, 'mouseup', () => { dragging = false })

                    self.google.maps.event.addListener(circle, 'center_changed', () => {
                        const center = circle.getCenter()
                        if (!dragging && (self.lastCenter.lat !== center.lat() || self.lastCenter.lng !== center.lng())) {
                            self.campaign.lat = center.lat()
                            self.campaign.lon = center.lng()
                            self.lastCenter.lat = center.lat()
                            self.lastCenter.lng = center.lng()
                            if (geocoder) {
                                reverseGeocode(geocoder, { lat: center.lat(), lng: center.lng() }).then(res => {
                                    self.campaign.address = res.formatted_address
                                    self.getTrafficVolume()
                                })
                            }
                        }
                    })

                    self.google.maps.event.addListener(circle, 'radius_changed', () => {
                        self.campaign.distance = circle.getRadius()
                        if (self.map) self.map.fitBounds(circle.getBounds())
                        self.getTrafficVolume()
                    })
                    clearInterval(interval)
                }
            }, 50)
        },

        // --- CAR TYPES & HELPERS ---
        selectType (type) {
            if (this.campaign.car_types.includes(type)) {
                const index = this.campaign.car_types.indexOf(type)
                this.campaign.car_types.splice(index, 1)
            } else {
                this.campaign.car_types.push(type)
            }
        },

        selectAllTypes () {
            if (this.campaign.car_types.length === 4) {
                this.campaign.car_types = []
            } else {
                this.campaign.car_types = [1, 2, 3, 4]
            }
        },

        sanitizeDeviceGroup (value) {
            return String(value || '')
        },

        cancelCampaign () {
            if (JSON.stringify(this.initialCampaign) !== JSON.stringify(this.campaign)) {
                toast.confirmCancel(() => this.$router.push({ name: 'Dashboard' }))
            } else {
                this.$router.push({ name: 'Dashboard' })
            }
        },

        // --- EDIT MODE RETRIEVAL ---
        getCampaign (id) {
            const url = '/get/campaign'
            const body = { data: { campaign_id: id } }

            this.request(url, 'POST', body).then(response => {
                if (response.code === 0 && response.data && response.data[0]) {
                    const data = response.data[0]
                    this.campaign = {
                        ...data,
                        device_group: data.device_group || ''
                    }
                    this.campaign.campaign_id = id

                    // Match city
                    if (data.address) {
                        for (const city of this.cityOptions) {
                            if (data.address.toLowerCase().includes(city.toLowerCase())) {
                                this.selectedCity = city
                                break
                            }
                        }
                    }

                    // Content preview
                    if (data.content_url) {
                        const isVid = data.content_url.match(/\.(mp4|mov|avi|flv)/i)
                        this.creativeType = isVid ? 'video' : 'image'
                        if (this.$refs.overflowIMG) {
                            this.$refs.overflowIMG.src = data.content_url
                            this.$refs.overflowIMG.style.opacity = 1
                        }
                    }

                    // Dates
                    if (data.start_date) this.campaign.start_date = new Date(data.start_date)
                    if (data.end_date) this.campaign.end_date = new Date(data.end_date)
                    if (data.start_time) this.campaign.start_time = data.start_time
                    if (data.end_time) this.campaign.end_time = data.end_time

                    // Limits & Totals
                    if (data.spend_limit) {
                        this.total_budget = Number(data.spend_limit)
                        this.typedBudget = this.total_budget
                        this.sliderBudgetValue = Math.min(10000, this.total_budget)
                        this.recalculateFromBudget(this.total_budget)
                    } else if (data.show_limit) {
                        this.total_impressions = Number(data.show_limit)
                        this.typedImpressions = this.total_impressions
                        this.sliderImpressionsValue = Math.min(2500000, this.total_impressions)
                        this.recalculateFromImpressions(this.total_impressions)
                    }

                    // Unlock all steps in edit mode so the user can freely jump around
                    this.maxStepReached = 6
                }
            })
        },

        getUserCampaigns () {
            this.request('/get/campaign/list', 'POST', {}).then(response => {
                if (response.code === 0) {
                    this.campaigns = response.data
                }
            }).catch(() => {})
        },

        checkTokenConditions (callback) {
            try {
                const expiresDate = new Date(this.token.expires)
                if (expiresDate <= new Date()) {
                    this.activeRequest = false
                    this.isProcessingPayment = false
                    this.store.commit('setToken', {})
                    this.$router.push('/login')
                    return
                }
            } catch (err) {
                this.activeRequest = false
                this.isProcessingPayment = false
                this.$router.push('/login')
                return
            }

            const body = { code: '', refresh_token: this.token.refresh_token }
            this.request('/refresh/token', 'POST', body).then(response => {
                if (response.code === 0) {
                    store.commit('setToken', response.data[0])
                    store.commit('setDriver', response.data[0].driver)
                    callback()
                } else {
                    this.activeRequest = false
                    this.isProcessingPayment = false
                    this.store.commit('setToken', {})
                    this.$router.push('/login')
                }
            }).catch(() => {
                this.activeRequest = false
                this.isProcessingPayment = false
            })
        },

        preloadStripe () {
            if (typeof window !== 'undefined' && !window.StripeCheckout) {
                if (!window.Stripe) {
                    const v2 = document.createElement('script')
                    v2.src = 'https://js.stripe.com/v2/'
                    document.head.appendChild(v2)
                }
                const script = document.createElement('script')
                script.src = 'https://checkout.stripe.com/checkout.js'
                document.head.appendChild(script)
            }
        }
    },

    created () {
        // Apply default flight date: 1st of next month for 1 month duration
        this.applyDatePreset('next_month')
        this.recalculateFromBudget(1000)
        this.initialCampaign = Object.assign({}, this.campaign)
    },

    mounted () {
        this.preloadStripe()
        this.getUserCampaigns()
        if (this.$route.params.id) {
            this.getCampaign(this.$route.params.id)
        }

        if (this.$refs.mapRef && this.$refs.mapRef.$mapPromise) {
            this.$refs.mapRef.$mapPromise.then(map => {
                this.map = map
                this.setCircleListeners()
                const zoomControlDiv = document.createElement('div')
                InitZoomControl(zoomControlDiv, map, this.google)
                const trafficVolumePosition = this.isMobile() ? 'TOP_LEFT' : 'RIGHT_BOTTOM'
                InitTrafficVolume(map, this.google, trafficVolumePosition)
            })
        }
    },

    watch: {
        google (val) {
            if (val) {
                geocoder = new window.google.maps.Geocoder()
            }
        }
    },

    components: {
        SmallCar,
        MediumCar,
        LuxuryCar,
        Truck,
        VueSlider,
        Datepicker,
        TimePicker,
        SideMenuHeader,
        ClipLoader,
        GeofenceIcon,
        ZipcodeIcon
    }
}
</script>

<style scoped>
/* ================= CONTAINER & BASE ================= */
.campaign__container {
    width: 100%;
    min-height: 100vh;
    margin: 0;
    text-align: left;
    box-sizing: border-box;
    position: relative;
    padding: 24px;
    background: #f8fafc;
    color: #1e293b;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.campaign__body {
    max-width: 1080px;
    margin: 0 auto;
    padding-bottom: 120px;
}

.campaign__header-wrap {
    margin-bottom: 28px;
}

.campaign__main-title {
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 6px 0;
}

.campaign__subtitle {
    font-size: 15px;
    color: #64748b;
    margin: 0;
}

/* ================= STEPPER PROGRESS BAR ================= */
.stepper__container {
    background: #ffffff;
    border-radius: 14px;
    padding: 16px 24px;
    margin-bottom: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
    border: 1px solid #e2e8f0;
}

.stepper__wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
}

.stepper__item {
    display: flex;
    align-items: center;
    position: relative;
    flex: 1;
}

.stepper__item--clickable {
    cursor: pointer;
}

.stepper__circle {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    margin-right: 10px;
    transition: all 0.25s ease;
    flex-shrink: 0;
}

.stepper__item--active .stepper__circle {
    background: #2ea3ff;
    color: #ffffff;
    box-shadow: 0 0 0 4px rgba(46, 163, 255, 0.2);
}

.stepper__item--completed .stepper__circle {
    background: #10b981;
    color: #ffffff;
}

.stepper__label {
    display: flex;
    flex-direction: column;
}

.stepper__step-tag {
    font-size: 11px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.stepper__title {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
}

.stepper__item--active .stepper__title {
    color: #0f172a;
}

.stepper__connector {
    flex: 1;
    height: 2px;
    background: #e2e8f0;
    margin: 0 12px;
}

.stepper__item--completed .stepper__connector {
    background: #10b981;
}

/* ================= STEP CARDS ================= */
.step-card {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    padding: 24px 28px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
}

.step-card--active {
    border-color: #2ea3ff;
    box-shadow: 0 10px 25px -5px rgba(46, 163, 255, 0.12), 0 8px 10px -6px rgba(46, 163, 255, 0.08);
}

.step-card--completed {
    border-color: #e2e8f0;
    opacity: 0.95;
}

.step-card--dimmed {
    opacity: 0.45;
    filter: grayscale(35%);
    pointer-events: none;
    user-select: none;
}

.step-card__header {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
}

.step-card__badge {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #f1f5f9;
    color: #0f172a;
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    flex-shrink: 0;
}

.step-card--active .step-card__badge {
    background: #2ea3ff;
    color: #ffffff;
}

.step-card--completed .step-card__badge {
    background: #ecfdf5;
    color: #10b981;
}

.step-card__header-info {
    flex: 1;
}

.step-card__header-info h2 {
    font-size: 19px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 2px 0;
}

.step-card__header-info p {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

.status-pill {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 12px;
    border-radius: 9999px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.status-pill--active {
    background: #eff6ff;
    color: #2ea3ff;
}

.status-pill--done {
    background: #f1f5f9;
    color: #475569;
}

.status-pill--locked {
    background: #f8fafc;
    color: #94a3b8;
}

.step-card__content {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #f1f5f9;
}

.step-card__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid #f1f5f9;
}

/* ================= FORM ELEMENTS ================= */
.form-row {
    display: flex;
    gap: 20px;
}

.flex-1 {
    flex: 1;
}

.form-group {
    display: flex;
    flex-direction: column;
}

.form-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 6px;
}

.bold-label {
    font-weight: 700;
    color: #0f172a;
}

.required {
    color: #ef4444;
}

.form-input, .form-select {
    height: 44px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    font-size: 14px;
    color: #1e293b;
    outline: none;
    transition: all 0.2s ease;
    width: 100%;
    box-sizing: border-box;
}

.form-input:focus, .form-select:focus {
    border-color: #2ea3ff;
    box-shadow: 0 0 0 3px rgba(46, 163, 255, 0.15);
}

.error-msg {
    font-size: 12px;
    color: #ef4444;
    margin-top: 4px;
}

/* ================= HELPER BUTTONS ================= */
.block-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
}

.helper-pill-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.helper-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
}

.helper-btn {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #334155;
    padding: 6px 14px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.helper-btn:hover {
    border-color: #2ea3ff;
    color: #2ea3ff;
    background: #eff6ff;
}

.helper-btn--active {
    background: #2ea3ff !important;
    color: #ffffff !important;
    border-color: #2ea3ff !important;
}

.helper-description {
    font-size: 12px;
    color: #64748b;
    margin: 2px 0 0 0;
}

/* ================= BUTTONS ================= */
.btn-continue {
    background: #2ea3ff;
    color: #ffffff;
    border: none;
    border-radius: 10px;
    height: 46px;
    padding: 0 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    box-sizing: border-box;
}

.btn-continue:hover {
    background: #1d8fe8;
}

.btn-back {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    height: 46px;
    padding: 0 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    box-sizing: border-box;
}

.btn-back:hover {
    background: #e2e8f0;
}

.btn-checkout {
    background: #10b981;
    color: #ffffff;
    border: none;
    border-radius: 10px;
    padding: 14px 28px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s;
}

.btn-checkout:hover {
    background: #059669;
}

.btn-secondary-save {
    background: transparent;
    color: #64748b;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    padding: 14px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

/* ================= STEP 1 ADVANCED ================= */
.advanced-section {
    margin-top: 18px;
    border-top: 1px dashed #e2e8f0;
    padding-top: 16px;
}

.btn-toggle-advanced {
    background: none;
    border: none;
    color: #2ea3ff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 6px;
}

.advanced-content {
    margin-top: 14px;
    background: #f8fafc;
    border-radius: 10px;
    padding: 16px;
}

/* ================= STEP 3 LOCATION ================= */
.location-top-bar {
    display: flex;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
}

.location-dropdown-group {
    flex: 1;
    min-width: 260px;
    margin-bottom: 0;
}

.location-dropdown-group .form-select {
    height: 48px;
    box-sizing: border-box;
}

.precise-geofence-toggle-wrap {
    flex: 1;
    min-width: 280px;
    margin-bottom: 0;
}

.checkbox-toggle-card {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 48px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.checkbox-toggle-card--active {
    border-color: #2ea3ff;
    background: #eff6ff;
}

.toggle-title {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    display: block;
    line-height: 1.2;
}

.toggle-desc {
    font-size: 11px;
    color: #64748b;
    display: block;
    line-height: 1.2;
}

.pair__label {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    display: block;
    margin-top: 16px;
    margin-bottom: 10px;
}

.address-autocomplete-input,
.location__details input:not([type="file"]):not([type="checkbox"]) {
    height: 44px;
    padding: 0 14px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    font-size: 14px;
    font-family: inherit;
    color: #1e293b;
    width: 100%;
    box-sizing: border-box;
    outline: none;
    transition: all 0.2s ease;
}

.address-autocomplete-input:focus,
.location__details input:focus {
    border-color: #2ea3ff;
    box-shadow: 0 0 0 3px rgba(46, 163, 255, 0.15);
}

.city-summary-pill {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
}

/* ================= STEP 4 CREATIVE ================= */
.detected-cpm-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 16px;
}

.detected-badge {
    font-size: 13px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 6px;
}

.detected-badge--video {
    background: #fef3c7;
    color: #b45309;
}

.detected-badge--image {
    background: #eff6ff;
    color: #1d4ed8;
}

.detected-cpm-text {
    font-size: 13px;
    color: #334155;
}

.info-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    font-size: 12px;
    color: #64748b;
}

.info-card strong {
    color: #0f172a;
    margin-bottom: 2px;
}

/* ================= STEP 5 BUDGET ================= */
.pricing-rate-card {
    display: flex;
    gap: 16px;
}

.rate-item {
    flex: 1;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 18px;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 0.2s ease;
}

.rate-item--selected {
    border-color: #2ea3ff;
    background: #eff6ff;
}

.rate-type {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 2px;
}

.rate-cost {
    font-size: 13px;
    color: #64748b;
}

.rate-tag {
    position: absolute;
    top: 12px;
    right: 14px;
    font-size: 11px;
    font-weight: 600;
    background: #2ea3ff;
    color: #ffffff;
    padding: 2px 8px;
    border-radius: 9999px;
}

.budget-control-section {
    background: #f8fafc;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    padding: 20px;
}

.budget-mode-tabs {
    display: flex;
    background: #e2e8f0;
    border-radius: 8px;
    padding: 3px;
    max-width: 320px;
    margin-bottom: 16px;
}

.budget-tab {
    flex: 1;
    background: transparent;
    border: none;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
}

.budget-tab--active {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}

.suggested-pills-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 16px;
}

.slider-typed-wrap {
    display: flex;
    align-items: center;
    gap: 24px;
}

.typed-input-wrap {
    position: relative;
    width: 170px;
    flex-shrink: 0;
}

.currency-symbol {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 16px;
    color: #64748b;
    font-weight: 600;
}

.typed-budget-input {
    width: 100%;
    height: 48px;
    padding: 0 12px 0 32px;
    border-radius: 10px;
    border: 1px solid #cbd5e1;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    box-sizing: border-box;
}

.slider-flex {
    flex: 1;
}

.slider-scale {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #94a3b8;
    margin-top: 4px;
}

/* ================= LIVE ESTIMATE CARD ================= */
.campaign-estimate-card {
    background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%);
    border: 1px solid #bfdbfe;
    border-radius: 14px;
    padding: 18px 22px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.estimate-icon {
    font-size: 24px;
}

.estimate-title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #1e40af;
    margin-bottom: 4px;
}

.estimate-body {
    font-size: 16px;
    line-height: 1.4;
    color: #0f172a;
    margin-bottom: 6px;
}

.estimate-footer {
    font-size: 13px;
    color: #475569;
}

/* ================= STEP 6 ORDER SUMMARY ================= */
.order-summary-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 24px;
}

.summary-top-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 20px;
    border-bottom: 1px solid #e2e8f0;
}

.summary-campaign-name {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.summary-client {
    font-size: 14px;
    color: #64748b;
    margin: 4px 0 0 0;
}

.summary-total-price {
    text-align: right;
}

.total-label {
    display: block;
    font-size: 12px;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.total-number {
    font-size: 26px;
    font-weight: 800;
    color: #10b981;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    padding: 20px 0;
    border-bottom: 1px solid #e2e8f0;
}

.summary-item-label {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-bottom: 2px;
}

.summary-item-val {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
}

.line-items-table {
    padding: 16px 20px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    margin-top: 16px;
}

.line-item {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: #475569;
    padding: 6px 0;
}

.line-item--total {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    padding: 0;
    border-top: none;
    margin-top: 0;
}

.policy-notice-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 18px;
    margin-top: 18px;
    display: flex;
    gap: 12px;
    font-size: 13px;
    color: #475569;
    line-height: 1.5;
}

/* ================= STICKY FOOTER ================= */
.sticky-footer-summary {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 74px;
    background: #ffffff;
    box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px;
    z-index: 1000;
    border-top: 1px solid #e2e8f0;
}

.sticky-left {
    display: flex;
    gap: 32px;
}

.sticky-metric {
    display: flex;
    flex-direction: column;
}

.metric-label {
    font-size: 11px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.metric-val {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
}

.sticky-right {
    display: flex;
    align-items: center;
    gap: 14px;
}

.btn-footer-cancel {
    background: none;
    border: none;
    color: #64748b;
    font-size: 14px;
    cursor: pointer;
    padding: 8px 16px;
}

.btn-footer-action {
    background: #2ea3ff;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

/* ================= PRESERVED MAP & CREATIVE STYLES ================= */
.google-type-container {
    display: flex;
    width: 100%;
    margin-bottom: 20px;
}
.google-type-holder {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    margin-right: 10px;
}
.google-type-holder:last-child {
    margin-right: 0;
}
.active-google-type-holder {
    border-color: #2ea3ff;
    background: #eff6ff;
}
.zip__input {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    min-height: 44px;
    margin-bottom: 12px;
}
.zip__input p {
    margin: 0;
    background: #2ea3ff;
    color: #ffffff;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 4px;
}
.zip__input svg {
    cursor: pointer;
    width: 14px;
    height: 14px;
}
.zip__input input {
    border: none;
    outline: none;
    flex: 1;
    min-width: 100px;
}
.upload__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #334155;
    color: #ffffff;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    margin-bottom: 4px;
}
.upload__label__note {
    font-size: 11px;
    color: #94a3b8;
    display: block;
}

.car__type__list {
    display: flex;
    gap: 12px;
    margin-top: 8px;
}
.car__type__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    cursor: pointer;
    background: #ffffff;
}
.car__type__item.active {
    border-color: #2ea3ff;
    background: #eff6ff;
}
.car__type__item.select__all {
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
}

.add__campaign__creative {
    margin-top: 10px;
}
.upload__container {
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    position: relative;
    background: #f8fafc;
    transition: all 0.2s;
}
.upload__container.drag__enter {
    border-color: #2ea3ff;
    background: #eff6ff;
}
.drag__img img {
    max-height: 80px;
    margin-bottom: 10px;
}
.info__container {
    display: flex;
    gap: 12px;
    margin-top: 14px;
}
.info__container > div {
    flex: 1;
}
.preview-wrap {
    position: relative;
    width: 320px;
    margin: 0 auto;
}
.preview-wrap img {
    width: 100%;
}
.overflow__img {
    position: absolute;
    top: 42%;
    left: 25%;
    width: 44% !important;
    height: 28%;
    object-fit: contain;
    border-radius: 4px;
}
#prevImgCanvas {
    position: absolute;
    top: 42%;
    left: 25%;
    width: 44%;
    height: 28%;
    object-fit: contain;
    border-radius: 4px;
}
#dragImgCanvas {
    display: none;
}

@media (max-width: 768px) {
    .stepper__wrapper {
        overflow-x: auto;
    }
    .form-row {
        flex-direction: column;
        gap: 12px;
    }
    .summary-grid {
        grid-template-columns: 1fr;
    }
    .slider-typed-wrap {
        flex-direction: column;
        align-items: stretch;
    }
    .sticky-footer-summary {
        padding: 0 16px;
    }
}
</style>
